import { useState, useEffect } from 'react';
import { Offering, OfferingsSearchPayload } from '../types/offerings';
import { getOfferingsSearch } from '../utils/data-fetch/getOfferingsSearch';

type RelaxedFilter = 'flexibility' | 'level';

const searchWithFallback = async (
  params: OfferingsSearchPayload,
): Promise<{ data: Offering[]; relaxedFilters: RelaxedFilter[] }> => {
  const relaxedFilters: RelaxedFilter[] = [];

  // TODO: The sorting model should be configured from edudirect-admin
  // Right now, this is hardcoded, when it should look at the admin and dynamicflow configuration
  // to determine if sorting should be applied
  // Like this:
  // const shouldUseSorting = result.sortingModel && ( result.sortingModel == 'fyp' || result.sortingModel == 'legacy' );
  // if (shouldUseSorting) {
  //   Extract sort options to preserve them across fallbacks
  // }
  // const sortOptions = {
  //   sortBy: params?.sortBy,
  //   sortUserContext: params?.sortUserContext,
  //   sortWebContext: params?.sortWebContext,
  //   sortListId: params?.sortListId,
  //   sortMaxResults: params?.sortMaxResults,
  // };
  const sortOptions = {};

  // Try with all filters first
  let response = await getOfferingsSearch(params);
  if (response.data.length > 0) return { data: response.data, relaxedFilters };

  // Fallback 1: Remove flexibility (and subType, since it may be tied to the flexibility selection)
  if (params.flexibility) {
    relaxedFilters.push('flexibility');
    response = await getOfferingsSearch({
      ...params,
      flexibility: undefined,
      subType: undefined,
      ...sortOptions,
    });
    if (response.data.length > 0)
      return { data: response.data, relaxedFilters };
  }

  // Fallback 2: Remove level (flexibility and subType already removed)
  if (params.level) {
    relaxedFilters.push('level');
    response = await getOfferingsSearch({
      ...params,
      level: undefined,
      flexibility: undefined,
      subType: undefined,
      ...sortOptions,
    });
    if (response.data.length > 0)
      return { data: response.data, relaxedFilters };
  }

  // Final fallback: Subject and skills (with sort options)
  response = await getOfferingsSearch({
    subjects: params.subjects,
    skills: params.skills,
    limit: params.limit,
    offset: params.offset,
    ...sortOptions,
  });
  return { data: response.data, relaxedFilters };
};

export const useOfferingSearch = (params: OfferingsSearchPayload) => {
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [relaxedFilters, setRelaxedFilters] = useState<RelaxedFilter[]>([]);

  useEffect(() => {
    let isCancelled = false;
    const fetchOfferings = async () => {
      setLoading(true);
      setError(null);
      setRelaxedFilters([]);
      try {
        const result = await searchWithFallback(params);
        if (isCancelled) return;
        setOfferings(result.data);
        setRelaxedFilters(result.relaxedFilters);
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching offerings:', err);
        setError(err);
        setOfferings([]);
        setRelaxedFilters([]);
      } finally {
        if (isCancelled) return;
        setLoading(false);
      }
    };

    fetchOfferings();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.query,
    // Stable serialization of arrays for dependency checking
    JSON.stringify(params.subjects),
    JSON.stringify(params.skills),
    params.level,
    params.flexibility,
    params.type,
    params.subType,
    params.limit,
    params.offset,
    // Sort options
    // These props were commented out as part of the fyp initiative
    // The comments should be handled once the implementation of the supersonic-ui is done
    // params.sortBy,
    // JSON.stringify(params.sortUserContext),
    // JSON.stringify(params.sortWebContext),
    // params.sortListId,
    // params.sortMaxResults,
  ]);

  return { offerings, loading, error, relaxedFilters };
};
